import { SheetSectionCard } from '@/components/SheetSectionCard'
import { IdentifierRow } from '../IdentifierRow'
import type { PatientRecord } from '@/features/patients/types'
import { mergeSourceIds } from '@/features/patients/utils/sourceIds'

/**
 * Identifiers, off the Overview: Careforce's own, then the source systems'.
 *
 * Nobody reads these while triaging a client, but reconciling one record
 * against eCW or Clarity needs all of them one click away — and each one
 * copyable, since they are transcribed into other systems rather than read.
 *
 * The Client ID is the record's id in Careforce (app_patients.id), the key
 * calls, visits, appointments and duties refer to the client by. It sits in a
 * section of its own so it is not mistaken for one more source system's id.
 */
export function ClientIdentifiersTab({ patient }: { patient: PatientRecord }) {
  const entries = mergeSourceIds(patient.source_ids)

  return (
    <div className="flex flex-col gap-4">
      <SheetSectionCard
        title="Careforce"
        body={
          <dl className="rounded-md border px-3">
            <IdentifierRow label="Client ID" value={patient.id} />
          </dl>
        }
      />
      <SheetSectionCard
        title={`Source system IDs${entries.length > 0 ? ` · ${entries.length}` : ''}`}
        body={
          entries.length === 0 ? (
            <p className="text-muted-foreground py-2 text-center text-sm">
              No source identifiers on file
            </p>
          ) : (
            <dl className="divide-y rounded-md border px-3">
              {entries.map((entry) => (
                <IdentifierRow
                  key={`${entry.system}:${entry.value}`}
                  label={entry.label}
                  value={entry.value}
                />
              ))}
            </dl>
          )
        }
      />
    </div>
  )
}
